import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { refreshToken } from '@/services/common/api';
import { redirectToSSO } from '@/utils/sso';

// 不需要登录的页面路由
const noLoginPath = ['/questionnaire'];

// 获取页面路由
const hashPath = window.location.hash;
const pathname = hashPath.replace('#', '').split('?')[0];

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number;
  message: string;
  requestToken: string;
  resultCode: string;
  resultObject: any;
  status?: string;
  traceId?: string;
  msg?: string;
}

/** 已在 clearTokensAndRedirectToSSO 中提示用户，errorHandler 不再重复弹窗 */
class AuthRedirectError extends Error {
  name = 'AuthRedirectError';
}

/** 登录服务请求不应携带业务 token，也不应走 401 刷新逻辑 */
function isLoginServerRequest(url?: string): boolean {
  return !!url && url.includes('/devops-login');
}

/**
 * 兼容 axios 0.x（headers.common）与 1.x（AxiosHeaders / 普通对象）
 */
function setAuthorizationHeader(
  headers: Record<string, any> | undefined,
  token: string,
): Record<string, any> {
  const authValue = `Bearer ${token}`;

  if (!headers) {
    return { Authorization: authValue };
  }

  if (typeof headers.set === 'function') {
    headers.set('Authorization', authValue);
    return headers;
  }

  if (headers.common && typeof headers.common === 'object') {
    headers.common.Authorization = authValue;
    return headers;
  }

  headers.Authorization = authValue;
  return headers;
}

/**
 * 清除 token 并跳转到 SSO 登录
 */
function clearTokensAndRedirectToSSO(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userCode');
  message.error('登录已失效，请重新登录');
  redirectToSSO();
}

/**
 * 处理 401 错误：尝试刷新 token
 */
async function handle401Error(options: RequestOptions): Promise<any> {
  const refreshTokenValue = localStorage.getItem('refreshToken');
  const userCode = localStorage.getItem('userCode') || undefined;

  // 如果没有 refreshToken 或 userCode，直接跳转到 SSO 登录
  if (!refreshTokenValue || !userCode) {
    clearTokensAndRedirectToSSO();
    return Promise.reject(new AuthRedirectError('Missing refreshToken or userCode'));
  }

  let refreshResponse;

  try {
    refreshResponse = await refreshToken(userCode, refreshTokenValue);
  } catch (error) {
    clearTokensAndRedirectToSSO();
    return Promise.reject(
      new AuthRedirectError(error instanceof Error ? error.message : 'Token refresh request failed'),
    );
  }

  // refreshToken 请求完成，检查结果（formatResult 可能已解包 resultObject）
  const newAccessToken = refreshResponse?.resultObject ?? refreshResponse;

  if (!newAccessToken) {
    clearTokensAndRedirectToSSO();
    return Promise.reject(new AuthRedirectError('Token refresh returned empty accessToken'));
  }

  // 刷新成功，更新 accessToken，重试原请求
  localStorage.setItem('accessToken', newAccessToken);

  const { request } = await import('@umijs/max');

  // @ts-ignore
  const originalUrl = options.originalUrl || options.url;

  // 构建重试请求配置
  const retryConfig: any = {
    method: options.method || 'GET',

    // 关键：标记这次请求已经是刷新 token 后的重试
    // 如果这次还 401，就不再继续 refreshToken，直接跳 SSO
    _retry: true,

    // 继续保留 originalUrl，避免请求拦截器重复处理时丢失原始地址
    // @ts-ignore
    originalUrl,
  };

  // 添加请求体（POST/PUT等）
  if (options.data !== undefined) {
    retryConfig.data = options.data;
  }

  // 添加查询参数（GET等）
  if (options.params !== undefined) {
    retryConfig.params = options.params;
  }

  retryConfig.headers = setAuthorizationHeader(options.headers, newAccessToken);

  const retryResponse = await request(originalUrl, retryConfig);

  return {
    data: retryResponse,
    status: 200,
    statusText: 'OK',
    headers: {},
    config: retryConfig,
  };
}

/**
 * @name 错误处理
 * pro 自带的错误处理，可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  errorConfig: {
    errorThrower: (res) => {
      // 根据实际响应结构处理错误
    },

    errorHandler: async (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 登录失效流程已提示并跳转 SSO，避免重复弹窗
      // 建议放前面，避免被 error.response 分支先命中
      if (error.name === 'AuthRedirectError') {
        console.error('[request auth error]', error.message);
        return;
      }

      if (error.name === 'BizError') {
        const errorInfo: any = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode, showType } = errorInfo;
          switch (showType) {
            case ErrorShowType.SILENT:
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        message.error('None response! Please retry.');
      } else {
        console.error('[request error]', error);
        const detail = error?.message ? `: ${error.message}` : '';
        message.error(`Request error, please retry${detail}`);
      }
    },
  },

  requestInterceptors: [
    (config: RequestOptions) => {
      // 保存原始 URL，用于响应拦截器重试
      // @ts-ignore
      if (!config.originalUrl) {
        // @ts-ignore
        config.originalUrl = config.url;
      }

      // 处理 URL：完整 URL 直接使用，相对路径添加 /titan 前缀
      if (config.url && !config.url.startsWith('http://') && !config.url.startsWith('https://')) {
        config.url = '/titan' + config.url;
      }

      // 添加 accessToken（登录服务接口除外）
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken && !isLoginServerRequest(config.url)) {
        config.headers = setAuthorizationHeader(config.headers, accessToken);
      }

      return config;
    },
  ],

  responseInterceptors: [
    // @ts-ignore - umi response interceptor type issue
    async (response: any, options: any) => {
      const data = response.data || {};
      const { code, status, msg } = data as ResponseStructure;

      const requestOptions = options || response?.config || {};
      const requestUrl = requestOptions.url || response?.config?.url;

      if (
        (code === 401 || status === '401') &&
        !isLoginServerRequest(requestUrl) &&
        !(noLoginPath.indexOf(pathname) > -1)
      ) {
        // 关键：如果已经是 refreshToken 后的重试请求，仍然 401
        // 说明新 token 也无效，直接跳登录，避免死循环
        if (requestOptions._retry) {
          clearTokensAndRedirectToSSO();
          return Promise.reject(new AuthRedirectError('Retry request still returned 401'));
        }

        return handle401Error(requestOptions);
      }

      // 处理其他错误
      if (status === 'false') {
        message.error('请求失败！');
      }

      if (code === 500 || status === '500') {
        if (msg) {
          message.error(msg);
        } else {
          message.error('服务器错误');
        }
      }

      return response || {};
    },
  ],
};